// ConfigureStepper.tsx
import * as React from "react";
import ConfigureServers from "./ConfigureStepperComponent/ConfigureServers";
import ConfigureSettings from "./ConfigureStepperComponent/ConfigureSettings";
import ConfigureTest from "./ConfigureStepperComponent/ConfigureTest";
import Toast from "../../Shared/Toast/Toast";
import { Loader2 } from "lucide-react";
import { useRedundancy } from "./hooks";
import { Sheet } from "../../ui/sheet";
import { Button } from "../../ui/button";

export default function ConfigureStepper({ open, handleClose }: { open: boolean; handleClose: () => void }) {
  const { serverConfig, handleActivateRedundancy, isLoading, testResult, steps, getButtonText, handleNext, handleBack, activeStep, isStepSkipped, resetStepper } = useRedundancy()
  const [isServerFormValid, setIsServerFormValid] = React.useState(false);
  const [isSettingsFormValid, setIsSettingsFormValid] = React.useState(false);

  const onClose = () => {
    resetStepper();
    setIsServerFormValid(false);
    setIsSettingsFormValid(false);
    handleClose();
  };

  const hasTestErrors = React.useMemo(() => {
    if (!testResult) return false;

    return !Object.values(testResult).every((value) =>
      typeof value === 'string' && value.toLowerCase() === 'pass'
    );
  }, [testResult]);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <ConfigureServers
          primary_address={serverConfig.primary_address}
          secondary_address={serverConfig.secondary_address}
          observer_address={serverConfig.observer_address}
          heartbeat_freqency={serverConfig.heartbeat_freqency}
          missed_heartbeat_threshold={serverConfig.missed_heartbeat_threshold}
          secondary_username={serverConfig.secondary_username}
          secondary_password={serverConfig.secondary_password}
          onValidationChange={setIsServerFormValid}
        />;
      case 1:
        return <ConfigureSettings
          onValidationChange={setIsSettingsFormValid}
        />;
      case 2:
        return <ConfigureTest testResult={testResult} errorMessage={null} />;
    }
  };

  const isNextButtonDisabled = () => {
    if (isLoading) return true;
    if (activeStep === 0) return !isServerFormValid;
    if (activeStep === 1) return !isSettingsFormValid;
    if (activeStep === steps.length - 1) return hasTestErrors;
    return false;
  };

  const footer = (
    <>
      <Button variant="outline" disabled={activeStep === 0} onClick={handleBack}>
        Back
      </Button>
      <Button
        onClick={activeStep === steps.length - 1 ? handleActivateRedundancy : handleNext}
        disabled={isNextButtonDisabled()}
      >
        {isLoading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          getButtonText()
        )}
      </Button>
    </>
  );

  return (
    <>
      <Toast />
      <Sheet open={open} onClose={onClose} title="Configure Redundancy" footer={footer} width="520px">
        {/* Stepper indicator */}
        <div className="flex items-center justify-between mb-6">
          {steps.map((label, index) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium shrink-0 ${
                index < activeStep
                  ? 'bg-primary text-primary-foreground'
                  : index === activeStep
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
              }`}>
                {index < activeStep ? '✓' : index + 1}
              </div>
              <span className={`text-sm ${
                index <= activeStep ? 'text-foreground font-medium' : 'text-muted-foreground'
              }`}>
                {label}
              </span>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-px mx-2 ${
                  index < activeStep ? 'bg-primary' : 'bg-border'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div>{renderStepContent(activeStep)}</div>
      </Sheet>
    </>
  );
}